import type { Nullable } from 'corgi-types';
import type { CheckoutError } from 'graphql-ts-types';

import { DEFAULT_CHECKOUT_ERROR, KNOWN_CHECKOUT_ERRORS } from '../../constants/checkout';
import type { ErrorMessage } from '../../types/error';
import { toArray } from '../array';
import { captureMessage } from '../error-reporter';

interface CheckoutResponse {
  errors: Nullable<Array<Pick<CheckoutError, 'error'>>>;
}

interface HasCheckoutErrorProps {
  responses: Array<Nullable<CheckoutResponse>> | Nullable<CheckoutResponse>;
  errorPattern: RegExp;
}

const ERROR_TRANSLATION_KEY_PREFIX = 'h24_checkout-error_';

export const getCheckoutErrorMessageKey = (errorDetail: string) => {
  const translationKey = `${ERROR_TRANSLATION_KEY_PREFIX}${errorDetail}`;

  if (KNOWN_CHECKOUT_ERRORS.includes(translationKey)) {
    return translationKey;
  }

  captureMessage(`Unknown errorDetail "${errorDetail}" thrown for CheckoutAlert`);

  return DEFAULT_CHECKOUT_ERROR;
};

export const getCheckoutErrorMessage = (
  errors?: Nullable<Array<Pick<CheckoutError, 'errorDetail'>>>
): ErrorMessage => {
  if ((errors?.length ?? 0) > 0) {
    const { errorDetail } = errors[0];

    return {
      message: getCheckoutErrorMessageKey(errorDetail),
      translated: false,
      timestamp: Date.now(),
    };
  }

  return {
    message: DEFAULT_CHECKOUT_ERROR,
    translated: false,
    timestamp: Date.now(),
  };
};

const hasCheckoutError = ({ responses, errorPattern }: HasCheckoutErrorProps) =>
  toArray(responses).some(
    response => response?.errors?.some(({ error }) => errorPattern.exec(error)) ?? false
  );

export const hasCheckoutAddressError = (...responses: CheckoutResponse[]) =>
  hasCheckoutError({ responses, errorPattern: /AddressError/ });

export const hasCheckoutPaymentError = (...responses: CheckoutResponse[]) =>
  hasCheckoutError({ responses, errorPattern: /PaymentError/ });
